import React, { ChangeEvent, FC, useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";

import {
  AnimalFragment,
  useGetAnimalsWithTagsLazyQuery,
  AnimalWithTagFragment,
  GetAnimalsWithTagsQueryVariables,
  AnimalSorting,
  useFieldNamesQuery,
  useGroupsInfoQuery,
} from "generated/graphql";

import cn from "classnames";
import { CellProps, Column } from "react-table";
import { debounce } from "lodash";
import { useHistory } from "react-router-dom";

import { TableOptionsContext } from "config/tableOptionsProvider";
import tablePaginationStyles from "components/Common/Table/TablePagination/TablePagination.module.scss";
import { getAnimalBreedsAsString, getMonthsAge } from "helpers/myLivestock";
import { getBUFromLocalStorage } from "helpers/storage";
import { useFormatDate, useOutsideClick, useStateSpecies } from "hooks";

import { AnimalActions } from "animals/AnimalActions";
import { AnimalsSexClassLabel } from "components/MyLivestock";
import { BaseInput } from "components/Common/Field/BaseInput";
import {
  Button,
  EmptyState,
  FieldDateRange,
  FieldSelect,
  Flex,
  Icon,
  InputCheckbox,
  LoadingOverlay,
  Spacer,
  Table,
  TableHeaderCaptions,
  Text,
} from "components";
import { Tag } from "tags/components/Tag";
import { COLUMN_IDS } from "components/MyLivestock/Animals/AnimalsTable";
import { TABLE_IDS } from "constants/Interface";
import { Checkbox, TablePaginationTarget } from "components/Common/Table";
import { Filters, PregnancyStatus, Props, Sex } from "./types";

import filterStyles from "components/MyLivestock/Animals/AnimalFiltersBlock/AnimalFiltersBlock.module.scss";
import paginationStyles from "components/Common/Table/TablePagination/TablePagination.module.scss";
import styles from "./AnimalsList.module.scss";
import { removeNothings } from "helpers/general";
import { motion } from "framer-motion";
import { areAllRowsSelected, getPageFrom, getPageTo } from "./helpers";
import { SelectedAnimalsTable } from "animals/SelectedAnimalsList";
import { getLoadingStateAnimals } from "animals/AnimalsList";
import { TablePaginationSizes, TABLE_PAGINATION_DEFAULT_OPTIONS } from "components/Common/Table/TablePagination";

const tableId = TABLE_IDS["ANIMAL_LIST_TAGS"];

const INITIAL_FILTERS: Filters = {
  fieldId: "",
  groupId: "",
  sex: "",
  pregnancyStatus: "",
  dateOfBirth: {
    from: undefined,
    to: undefined,
  },
  dateOnFarm: {
    from: undefined,
    to: undefined,
  },
};

export const AnimalsListCursorPaginated: FC<Props> = ({
  fromPage,
  tagId,
  showSelectColumn,
  showAnimalActionButtons,
}) => {
  const businessUnitId = getBUFromLocalStorage();
  const history = useHistory();
  const { getTableOptions, setTableOptions } = useContext(TableOptionsContext);
  const [sortBy, setSortBy] = React.useState<AnimalSorting>();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isSelectedAnimalsView, setIsSelectedAnimalsView] = useState(false);
  const { getFormatDate } = useFormatDate();
  const { active: speciesActive } = useStateSpecies();
  const [pageNumber, setPageNumber] = useState(1);

  const [showFilters, setShowFilters] = useState<boolean>(false);
  const { data: fieldsData } = useFieldNamesQuery({
    variables: {
      businessUnitId,
    },
  });
  const fieldOptions = removeNothings(fieldsData?.fields ?? []);

  const { data: groupsData } = useGroupsInfoQuery({
    variables: {
      businessUnitId,
    },
  });

  const groups = removeNothings(groupsData?.groupsExtended?.groups ?? []);

  const tableOptions = getTableOptions<AnimalWithTagFragment>(tableId);

  const sortByValue = tableOptions?.sortBy;
  const selectedRowIds = tableOptions?.selectedRowsIds;

  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);

  const [firstCursor, setFirstCursor] = React.useState<null | string>(null);

  const [pageSize, setPageSize] = React.useState<TablePaginationSizes>(10);
  const [hasNext, setHasNext] = React.useState<boolean>(false);

  const [pagination, setPagination] = React.useState<{
    last?: number;
    before?: string | null;
    first?: number;
    after?: string;
  }>({
    first: pageSize,
  });
  const [getAnimals, { data, loading, refetch }] = useGetAnimalsWithTagsLazyQuery({
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });
  const [showPageSizeOptionsMenu, setShowPageSizeOptionsMenu] = useState<boolean>(false);

  const totalNumberOfAnimals = data?.animals?.count ?? 0;
  const pageIndex = pageNumber - 1;
  const pageFrom = getPageFrom({ totalNumberOfAnimals, pageSize, pageIndex });
  const pageTo = getPageTo({ totalNumberOfAnimals, pageSize, pageFrom });

  const pageInfo = data?.animals?.pageInfo ?? undefined;

  const togglePageSizeOptionsMenu = () => {
    setShowPageSizeOptionsMenu((state) => !state);
  };
  const hidePageSizeOptionsMenu = () => {
    setShowPageSizeOptionsMenu(false);
  };

  const pageSizeMenuRef = useRef(null);
  useOutsideClick(pageSizeMenuRef, hidePageSizeOptionsMenu);

  useEffect(() => {
    const variables: GetAnimalsWithTagsQueryVariables = {
      search: searchTerm,
      isMale: filters.sex === Sex.MALE ? true : filters.sex === Sex.FEMALE ? false : undefined,
      isPregnant:
        filters.pregnancyStatus === PregnancyStatus.PREGNANT
          ? true
          : filters.pregnancyStatus === PregnancyStatus.NOTPREGNANT
          ? false
          : undefined,
      minDob: filters.dateOfBirth.from || undefined,
      maxDob: filters.dateOfBirth.to || undefined,
      minDof: filters.dateOnFarm.from || undefined,
      maxDof: filters.dateOnFarm.to || undefined,
      field: filters.fieldId || undefined,
      group: filters.groupId || undefined,
      businessUnitId: businessUnitId,
      last: pagination.last,
      before: pagination.before,
      first: pagination.first,
      after: pagination.after,
      orderBy: sortBy ? [sortBy] : undefined,
      animalTags: tagId ? [tagId] : undefined,
    };

    getAnimals({ variables });
  }, [
    getAnimals,
    sortBy,
    pageSize,
    tagId,
    businessUnitId,
    filters.fieldId,
    filters.groupId,
    filters.sex,
    filters.pregnancyStatus,
    filters.dateOfBirth.from,
    filters.dateOfBirth.to,
    filters.dateOnFarm.from,
    filters.dateOnFarm.to,
    searchTerm,
    pagination,
  ]);

  const animals = useMemo(
    () =>
      removeNothings(
        data?.animals?.edges.map((animal) => {
          return animal?.node;
        }) ?? [],
      ),
    [data],
  );
  useEffect(() => {
    if (pageInfo?.startCursor) {
      if (firstCursor == null && firstCursor != pageInfo.startCursor) {
        setFirstCursor(pageInfo.startCursor);
      }
    }
  }, [firstCursor, pageInfo]);

  useEffect(() => {
    if (sortByValue) {
      setPageNumber(1);
      setSortBy(sortRuleToAnimalSorting(sortByValue[0]));
    }
  }, [sortByValue]);

  useEffect(() => {
    setPageNumber(1);
  }, [filters]);

  // INFO: This smells but is this only current way to fix
  // the broken relay pagination. Currently hasNextPage comes
  // back from the api but does not always give the correct value
  // so we are having to do it on the frontend
  React.useEffect(() => {
    if (animals.length === 0) {
      setHasNext(false);
    } else {
      setHasNext(true);
    }
  }, [animals]);

  const selectedAnimalIds = useMemo(() => (selectedRowIds ? [...selectedRowIds] : []), [selectedRowIds]);

  const onCellClick = useCallback(
    (animal: AnimalFragment) => {
      history.push(`/system/${fromPage || "livestock"}/animal/${animal.id}`);
    },
    [history, fromPage],
  );

  const handleChangePageSize = (size: TablePaginationSizes) => {
    setPageNumber(1);
    setPageSize(size);
    hidePageSizeOptionsMenu();
    setPagination({
      first: size,
    });
  };

  // @ts-expect-error
  const columns: Column<AnimalWithTagFragment>[] = useMemo(
    () => [
      {
        id: COLUMN_IDS.SELECTION,
        Header: function SelectAllHeader({ data }) {
          const animalIds: Array<string> = data.map((a: AnimalWithTagFragment) => a.id);
          const selectedRowsIds = tableOptions?.selectedRowsIds;

          const checked = areAllRowsSelected(animalIds, selectedRowsIds ?? undefined);

          const updatedRows = new Set(
            Array.from(selectedRowsIds ?? []).filter((rowId) => {
              return !animalIds.includes(rowId);
            }),
          );

          const handleCheckboxChange = (): void => {
            if (checked) {
              setTableOptions({
                id: tableId,
                selectedRowsIds: updatedRows,
              });
            } else {
              const ids = new Set<string>(selectedRowsIds);

              data.forEach((item: AnimalWithTagFragment) => {
                ids.add(item["id"]);
              });

              setTableOptions({
                id: tableId,
                selectedRowsIds: ids,
              });
            }
          };
          return showSelectColumn ? <InputCheckbox checked={checked} onChange={handleCheckboxChange} /> : null;
        },
        Cell: function SelectColumn(
          props: React.PropsWithChildren<CellProps<AnimalWithTagFragment, string | null | undefined>>,
        ): React.ReactNode {
          const id = props.cell.row.original.id;

          return showSelectColumn ? <Checkbox tableId={tableId} id={id} /> : null;
        },
        sticky: "left",
        minWidth: showSelectColumn ? 40 : 0,
        width: showSelectColumn ? 40 : 0,
      },
      {
        id: COLUMN_IDS.ANIMAL_ID,
        Header: "Animal ID",
        accessor: "passportNumber",
        onCellClick,
        sticky: "left",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.E_ID,
        Header: "EID",
        accessor: "eId",
        onCellClick,
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.E_ID] || "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.VISUAL_ID,
        Header: "Visual ID",
        accessor: "visualId",
        onCellClick,
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.VISUAL_ID] || "\u2014",
        minWidth: 150,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.BREED,
        Header: "Breed",
        accessor: (row): string => getAnimalBreedsAsString(row.animalBreeds),
        onCellClick,
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.BREED] || "\u2014",
        minWidth: 150,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.SEX,
        Header: "Sex",
        accessor: ({ isMale, sexClassification }): string => sexClassification?.slug || (isMale ? "M" : "F"),
        onCellClick,
        Cell: function SexCell({ row }): React.ReactNode {
          const { id, isCastrated, isMale, sexClassification } = row?.original;

          return (
            <AnimalsSexClassLabel
              id={`animal_sex_label__${id}`}
              isCastrated={isCastrated}
              isMale={isMale}
              label={sexClassification?.title || null}
            />
          );
        },
        minWidth: 100,
        width: 100,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.TAGS,
        Header: "Tags",
        accessor: "tags",
        onCellClick,
        Cell: function TagsCell({ row }) {
          const { tags } = row?.original;

          return tags.map((tag) => {
            const t = {
              tagId: tag.tagId,
              name: tag.name,
              color: tag.color,
              ownership: tag?.schema?.ownership,
            };
            return <Tag key={tag.tagId} tag={t} />;
          });
        },
        minWidth: 100,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.GROUP,
        Header: "Group",
        accessor: ({ group }): string | null => group?.name || null,
        onCellClick,
        Cell: ({ row }): React.ReactElement => row.values[COLUMN_IDS.GROUP] || "\u2014",
        minWidth: 150,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.FIELD,
        Header: "Location",
        accessor: ({ field }): string | null => field?.name || null,
        onCellClick,
        Cell: ({ row }): React.ReactElement => row.values[COLUMN_IDS.FIELD] || "\u2014",
        minWidth: 150,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.AGE,
        Header: <TableHeaderCaptions title="Age" subtitle="Months" />,
        accessor: ({ dateLeftFarm, deadAt, dob }): number | null => {
          // if dead, calculate age at death
          if (deadAt) return getMonthsAge(dob, deadAt);

          // if off farm, calculate age at move off farm
          if (dateLeftFarm) return getMonthsAge(dob, dateLeftFarm);

          // otherwise just calculate current age
          return getMonthsAge(dob);
        },
        onCellClick,
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.AGE] || "\u2014",
        minWidth: 100,
        width: 100,
      },
      {
        id: COLUMN_IDS.DATE_OF_BIRTH,
        Header: "DoB",
        accessor: "dob",
        Cell: ({ row }): React.ReactNode =>
          row.values[COLUMN_IDS.DATE_OF_BIRTH] ? getFormatDate(row.values[COLUMN_IDS.DATE_OF_BIRTH]) : "\u2014",
        onCellClick,
        minWidth: 120,
        width: 120,
      },
      {
        id: COLUMN_IDS.DATE_ON_FARM,
        Header: "Date on farm",
        accessor: "dateMovedToFarm",
        onCellClick,
        Cell: ({ value }): React.ReactNode => (value ? getFormatDate(value) : "\u2014"),
        minWidth: 120,
        width: 120,
      },
    ],
    [onCellClick, getFormatDate, showSelectColumn, tableOptions?.selectedRowsIds, setTableOptions],
  );

  const onChangeSearchTerm = (e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value);

  const debouncedSearchTerm = useMemo(() => {
    return debounce(onChangeSearchTerm, 300);
  }, []);

  useEffect(() => {
    return () => {
      debouncedSearchTerm.cancel();
    };
  }, [debouncedSearchTerm]);

  useEffect(() => {
    return () => {
      setTableOptions({ selectedRows: undefined, selectedRowsIds: undefined, id: tableId });
    };
  }, [setTableOptions]);

  useEffect(() => {
    if (isSelectedAnimalsView && !selectedAnimalIds.length) {
      setIsSelectedAnimalsView(false);
    }
  }, [selectedAnimalIds, isSelectedAnimalsView]);

  const caption = `${selectedAnimalIds?.length} Selected - View selection`;

  const animalsData = useMemo(
    () => (loading && animals.length === 0 ? getLoadingStateAnimals(pageSize) : animals),
    [animals, loading, pageSize],
  );

  const canPrevPage = () => {
    if (firstCursor === pageInfo?.startCursor || firstCursor == null) {
      return false;
    }

    return true;
  };

  if (!speciesActive) {
    return <LoadingOverlay />;
  }

  if (isSelectedAnimalsView) {
    return (
      <>
        <Flex item itemGutter>
          <Spacer baselineHeight={1} />

          {!!selectedAnimalIds.length && showAnimalActionButtons && <AnimalActions animalIds={selectedAnimalIds} />}
          <Spacer baselineHeight={1} />

          <Flex
            className={paginationStyles.table_pagination_target}
            container
            containerAlignItems="center"
            containerJustifyContent="flex-end"
            containerWrap="wrap"
          >
            <Button
              caption="Back to list"
              colour="grey"
              variant="hollow"
              onClick={() => setIsSelectedAnimalsView(false)}
            />
            <Flex item itemGrow />
            <TablePaginationTarget tableId={TABLE_IDS["ANIMAL_LIST_SERVER_SELECTED"]} />
            <div style={{ width: "10px" }} />
          </Flex>
        </Flex>

        <Spacer baselineHeight={1} />

        <Flex item itemGutter xs="fill">
          <SelectedAnimalsTable columns={columns} sourceTableId={tableId} />
        </Flex>
      </>
    );
  }

  return (
    <>
      <Flex item itemGutter>
        <Spacer baselineHeight={1} />

        <motion.div
          animate={
            !selectedAnimalIds.length || !showAnimalActionButtons
              ? {
                  opacity: 0,
                  visibility: "hidden",
                }
              : {
                  opacity: 1,
                  visibility: "visible",
                }
          }
        >
          <AnimalActions animalIds={selectedAnimalIds} />
        </motion.div>

        <Spacer baselineHeight={1} />

        <Flex container containerJustifyContent="space-between">
          <Flex container>
            <Flex className={styles.search_input} item>
              <BaseInput type="text" onChange={debouncedSearchTerm} name="animal_search" placeholder="Search by ID" />
            </Flex>

            <motion.div
              animate={
                !showSelectColumn || !selectedAnimalIds.length
                  ? {
                      opacity: 0,
                      visibility: "hidden",
                    }
                  : {
                      opacity: 1,
                      visibility: "visible",
                    }
              }
            >
              <Flex item itemGutter>
                <Button caption={caption} onClick={() => setIsSelectedAnimalsView((state) => !state)} colour="green" />
              </Flex>
            </motion.div>
          </Flex>

          <Flex container containerAlignItems="center">
            <Flex
              className={tablePaginationStyles.table_pagination_target}
              container
              containerAlignItems="center"
              containerJustifyContent="flex-end"
              containerWrap="wrap"
            >
              <Flex item itemGrow />
              {pageInfo != null && typeof refetch == "function" && (
                <>
                  <Flex item container>
                    <button
                      className={cn(
                        paginationStyles.table_pagination__button,
                        paginationStyles["table_pagination__button--first"],
                      )}
                      disabled={!canPrevPage()}
                      onClick={() => {
                        setPageNumber(1);
                        refetch({
                          first: pageSize,
                          after: undefined,
                          last: undefined,
                          before: undefined,
                          businessUnitId,
                        });
                      }}
                      title={`First page`}
                    >
                      <Icon name="doubleChevron" rotate={180} size="atom" />
                    </button>

                    <button
                      className={cn(
                        paginationStyles.table_pagination__button,
                        paginationStyles["table_pagination__button--prev"],
                      )}
                      title="Previous page"
                      disabled={!canPrevPage()}
                      onClick={() => {
                        setHasNext(true);
                        refetch({
                          first: undefined,
                          after: undefined,
                          last: pageSize,
                          before: pageInfo.startCursor,
                          businessUnitId,
                        });
                        setPageNumber((pageNumber) => pageNumber - 1);
                      }}
                    >
                      <Icon name="chevron" rotate={180} size="atom" />
                    </button>
                  </Flex>

                  <Flex item>
                    <>
                      <div className={paginationStyles.table_pagination__per_page} ref={pageSizeMenuRef}>
                        <button
                          className={cn(paginationStyles.table_pagination__current_page, {
                            [paginationStyles["table_pagination__current_page--active"]]: showPageSizeOptionsMenu,
                          })}
                          type="button"
                          onClick={togglePageSizeOptionsMenu}
                          title="Rows per page"
                        >
                          <Flex container>
                            <Flex item itemGutter>
                              {pageFrom} to {pageTo}
                            </Flex>
                            <Flex item>
                              <Icon name="rectangle" size="atom" colour="grey_dark" />
                            </Flex>
                          </Flex>
                        </button>

                        {showPageSizeOptionsMenu && (
                          <div className={paginationStyles.table_pagination__page_sizes}>
                            <span className={paginationStyles.table_pagination__page_sizes_title}>Rows:</span>

                            {TABLE_PAGINATION_DEFAULT_OPTIONS.SIZES.map((size) => (
                              <span
                                className={cn(paginationStyles.table_pagination__page_size, {
                                  [paginationStyles["table_pagination__page_size--active"]]: size === pageSize,
                                })}
                                key={size}
                                onClick={() => handleChangePageSize(size)}
                                onKeyUp={() => handleChangePageSize(size)}
                                tabIndex={0}
                              >
                                {size}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <span className={paginationStyles.table_pagination__total_items}>of {totalNumberOfAnimals}</span>
                    </>
                  </Flex>

                  <Flex item container>
                    <button
                      title="Next page"
                      disabled={!hasNext}
                      onClick={() => {
                        refetch({
                          first: pageSize,
                          after: pageInfo.endCursor,
                          last: undefined,
                          before: undefined,
                          businessUnitId: businessUnitId,
                        });
                        setPageNumber((pageNumber) => pageNumber + 1);
                      }}
                      className={cn(
                        paginationStyles.table_pagination__button,
                        paginationStyles["table_pagination__button--next"],
                      )}
                    >
                      <Icon name="chevron" size="atom" />
                    </button>
                    <button
                      className={cn(
                        paginationStyles.table_pagination__button,
                        paginationStyles["table_pagination__button--last"],
                      )}
                      disabled={!hasNext}
                      onClick={() => {
                        refetch({
                          first: undefined,
                          after: undefined,
                          last: totalNumberOfAnimals % pageSize,
                          before: undefined,
                          businessUnitId,
                        });
                        setPageNumber(Math.ceil(totalNumberOfAnimals / pageSize));
                      }}
                      title="Last page"
                    >
                      <Icon name="doubleChevron" size="atom" />
                    </button>
                  </Flex>
                </>
              )}
            </Flex>
            <div style={{ width: 10 }} />

            <button
              className={cn(filterStyles.toggle_button, {
                [filterStyles["toggle_button--active"]]: showFilters,
              })}
              onClick={() => setShowFilters((prevState) => !prevState)}
            >
              <Text smaller>Filters</Text>

              <Icon className={filterStyles.toggle_button__icon} name="filter" colour="black" size="small" />
            </button>
          </Flex>
        </Flex>
      </Flex>

      {/* Filters block */}
      {showFilters && (
        <Flex item itemGutter>
          <Flex className={cn(styles.filtersContainer)} container>
            <Flex item itemGutter xs={12} s={6} m={3} xl={2}>
              <FieldSelect
                label="Sex"
                inputProps={{
                  name: "sex",
                  value: filters.sex ?? "",
                  placeholder: "No preference",
                  options: [
                    {
                      key: 1,
                      value: Sex.MALE,
                      label: "Male",
                    },
                    {
                      key: 2,
                      value: Sex.FEMALE,
                      label: "Female",
                    },
                  ],
                  onChange: (event: ChangeEvent<HTMLInputElement>) =>
                    setFilters({ ...filters, sex: event.target.value }),
                  isClearable: true,
                }}
              />
            </Flex>
            <Flex item itemGutter xs={12} s={6} m={3} xl={2}>
              <FieldSelect
                label="Pregnancy status"
                inputProps={{
                  name: "pregnancyStatus",
                  value: filters.pregnancyStatus ?? "",
                  placeholder: "No preference",
                  options: [
                    {
                      key: 1,
                      value: PregnancyStatus.PREGNANT,
                      label: "Pregnant",
                    },
                    {
                      key: 2,
                      value: PregnancyStatus.NOTPREGNANT,
                      label: "Not pregnant",
                    },
                  ],
                  onChange: (event: ChangeEvent<HTMLInputElement>) =>
                    setFilters({ ...filters, pregnancyStatus: event.target.value }),
                  isClearable: true,
                }}
              />
            </Flex>
            <Flex item itemGutter xs={12} s={6} m={3} xl={2}>
              <FieldSelect
                label="Group"
                inputProps={{
                  name: "groups",
                  value: groups.find((group) => group.id === filters.groupId)?.name ?? "",
                  multiple: false,
                  options: groups,
                  placeholder: "No preference",
                  onChange: (event: ChangeEvent<HTMLInputElement>) =>
                    setFilters({ ...filters, groupId: event.target.value }),
                  labelKey: "name",
                  valueKey: "id",
                  isClearable: true,
                }}
              />
            </Flex>
            <Flex item itemGutter xs={12} s={6} m={3} xl={2}>
              <FieldSelect
                label={"Location"}
                inputProps={{
                  name: "fields",
                  value: fieldOptions.find((field) => field.id === filters.fieldId)?.name ?? "",
                  multiple: false,
                  options: fieldOptions,
                  placeholder: "No preference",
                  onChange: (event: ChangeEvent<HTMLInputElement>) =>
                    setFilters({ ...filters, fieldId: event.target.value }),
                  labelKey: "name",
                  valueKey: "id",
                  isClearable: true,
                }}
              />
            </Flex>
            <Flex item itemGutter xs={12} m={6} xl={4}>
              <FieldDateRange
                label="DoB range"
                inputProps={{
                  from: {
                    value: filters.dateOfBirth.from,
                    name: "dobDateFrom",
                    onChange: (date: Date) =>
                      setFilters({ ...filters, dateOfBirth: { ...filters.dateOfBirth, from: date } }),
                    placeholderText: "No preference",
                    isClearable: true,
                  },
                  to: {
                    value: filters.dateOfBirth.to,
                    name: "dobDateTo",
                    onChange: (date: Date) =>
                      setFilters({ ...filters, dateOfBirth: { ...filters.dateOfBirth, to: date } }),
                    placeholderText: "No preference",
                    isClearable: true,
                  },
                }}
              />
            </Flex>
            <Flex item itemGutter xs={12} m={6} xl={4}>
              <FieldDateRange
                label="Date on farm range"
                inputProps={{
                  from: {
                    value: filters.dateOnFarm.from,
                    name: "dofDateFrom",
                    onChange: (date: Date) =>
                      setFilters({ ...filters, dateOnFarm: { ...filters.dateOnFarm, from: date } }),
                    placeholderText: "No preference",
                    isClearable: true,
                  },
                  to: {
                    value: filters.dateOnFarm.to,
                    name: "dofDateTo",
                    onChange: (date: Date) =>
                      setFilters({ ...filters, dateOnFarm: { ...filters.dateOnFarm, to: date } }),
                    placeholderText: "No preference",
                    isClearable: true,
                  },
                }}
              />
            </Flex>
          </Flex>
        </Flex>
      )}
      {/* End of filters block */}

      <Spacer baselineHeight={1} />

      {animals.length === 0 && !loading ? (
        <EmptyState page="TAG" />
      ) : (
        <Flex container item itemGrow>
          <Flex item itemGutter xs="fill">
            {loading && <LoadingOverlay />}
            <Table<AnimalWithTagFragment>
              data={animalsData}
              isAnimals={!(fromPage === "field")}
              columns={columns}
              tableId={tableId}
              manualSortBy={true}
              manualPagination={true}
              pagination={true}
              initialPageSize={pageSize}
            />
          </Flex>
        </Flex>
      )}
    </>
  );
};

const sortRulesByColumnId = {
  [COLUMN_IDS.ANIMAL_ID]: {
    asc: AnimalSorting.PassportNumberAsc,
    desc: AnimalSorting.PassportNumberDesc,
  },
  [COLUMN_IDS.E_ID]: {
    asc: AnimalSorting.EIdAsc,
    desc: AnimalSorting.EIdDesc,
  },
  [COLUMN_IDS.AGE]: {
    asc: AnimalSorting.DobAsc,
    desc: AnimalSorting.DobDesc,
  },
  [COLUMN_IDS.DATE_OF_BIRTH]: {
    asc: AnimalSorting.DobAsc,
    desc: AnimalSorting.DobDesc,
  },
  [COLUMN_IDS.DATE_ON_FARM]: {
    asc: AnimalSorting.TimeOnFarmAsc,
    desc: AnimalSorting.TimeOnFarmDesc,
  },
} as const;

const sortRuleToAnimalSorting = (sortRule: { id: string; desc: boolean }): AnimalSorting | undefined => {
  const sortingRule = sortRulesByColumnId[sortRule?.id];
  return sortingRule?.[sortRule.desc ? "desc" : "asc"];
};
